// Column sets for the Item/Lot lot-keyed sub-tabs (Open PO's, OM Transactions, Lot/Locations), served by
// ItemManagementController → camelCase. Captions mirror the Delphi grids; a small visible set, rest in the
// column chooser. Legacy currency/foreign/GL and Price A-E (SQL-function) columns are omitted.
import { STATUSES, STAGES, OM_TRANSACTION_TYPES, INVENTORY_TYPES } from "../transactionsTab/codeLists";

const money = { type: "fixedPoint", precision: 2 };
const txt = (dataField, caption, visible = false) => ({ dataField, caption, visible });
const num = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "number", format: money });
const dt = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "date", format: "MM/dd/yyyy" });
const dtm = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "datetime", format: "MM/dd/yyyy hh:mm a" });
// Coded field → descriptive dropdown (codeLists {code,label} lists).
const lkc = (dataField, caption, dataSource, visible = false) => ({ dataField, caption, visible, lookup: { dataSource, valueExpr: "code", displayExpr: "label" } });
// User-defined passthroughs: `${labelPrefix} ${n} - ${scope}`.
const udf = (fieldPrefix, labelPrefix, scope, count, kind) =>
  Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const field = `${fieldPrefix}${n}`;
    const label = `${labelPrefix} ${n} - ${scope}`;
    return kind === "date" ? dt(field, label) : kind === "num" ? num(field, label) : txt(field, label);
  });

// "Open PO's" (Delphi frmIMM0060).
export const OPEN_PO_COLUMNS = [
  txt("poNumber", "PO Number", true),
  txt("vendorId", "Vendor", true),
  txt("vendorName", "Vendor Name", true),
  dt("poDate", "PO Date", true),
  lkc("inventoryType", "Type", INVENTORY_TYPES, true),
  num("orderedQuantity", "Ordered", true),
  num("unitCostOriginal", "Unit Cost", true),
  num("amount", "Amount", true),
  txt("purpose", "Purpose", true),
  txt("lotCode", "Lot", true),
  // Hidden.
  txt("recordNumber", "Record #"),
  txt("poDescription", "PO Description"),
  dt("hdrDateRequired", "Required"),
  dt("hdrDateExpected", "Expected"),
  num("itemDiscount", "Item Discount"),
  txt("referenceNumber", "PO Ref Number"),
  dt("detailDateRequired", "Detail Date Required"),
  dt("detailDateExpected", "Detail Date Expected"),
  dt("detailDateReceived", "Detail Date Received"),
  dtm("creationDateTime", "Creation Date"),
  txt("creationUserId", "Created By"),
  dtm("lastUpdateDateTime", "Last Updated"),
  txt("lastUpdateUserId", "Last Updated By"),
  ...udf("headerCode", "Code", "PO Header", 10, "txt"),
  ...udf("headerReference", "Reference", "PO Header", 10, "txt"),
  ...udf("headerDate", "Date", "PO Header", 10, "date"),
  ...udf("headerQuantity", "Quantity", "PO Header", 10, "num"),
  ...udf("detailCode", "Code", "Detail", 5, "txt"),
  ...udf("detailReference", "Reference", "Detail", 5, "txt"),
  ...udf("detailDate", "Date", "Detail", 5, "date"),
  ...udf("detailQuantity", "Quantity", "Detail", 5, "num"),
  ...udf("lotCode", "Code", "Lot", 10, "txt"),
  ...udf("lotReference", "Reference", "Lot", 10, "txt"),
  ...udf("lotDate", "Date", "Lot", 10, "date"),
  ...udf("lotQuantity", "Quantity", "Lot", 10, "num"),
];

// "OM Transactions" (Delphi frmIMM0063).
export const OM_TRANSACTION_COLUMNS = [
  lkc("transactionType", "Transaction Type", OM_TRANSACTION_TYPES, true),
  txt("transactionNumber", "Transaction #", true),
  dt("transactionDate", "Transaction Date", true),
  txt("customerId", "Customer", true),
  txt("itemCode", "Item", true),
  txt("lotCode", "Lot", true),
  num("quantityOrdered", "Ordered", true),
  num("quantityShipped", "Shipped", true),
  num("amount", "Amount", true),
  lkc("status", "Status", STATUSES, true),
  // Hidden — header.
  txt("recordNumber", "Record #"),
  dt("invoiceDate", "Invoice Date"),
  dt("transactionDueDate", "Due Date"),
  txt("purchaseOrderNumber", "PO #"),
  txt("priceLevel", "Price Level"),
  txt("discountType", "Discount Type"),
  txt("headerReference", "Header Reference"),
  txt("headerComment", "Header Comment"),
  txt("shipperId", "Ship To"),
  txt("shipToName", "Ship To Name"),
  txt("consigneeId", "Consignee"),
  dt("shippingDate", "Shipping Date"),
  txt("shipperComment", "Shipper Comment"),
  txt("headerUserComment", "Header User Comment"),
  num("headerDiscount", "Header Discount"),
  txt("reasonCode", "Reason Code"),
  num("fiscalPeriod", "Fiscal Period"),
  num("fiscalYear", "Fiscal Year"),
  txt("fmTripNumber", "FM Trip Number"),
  num("fmPuStopNumber", "FM Stop Number"),
  lkc("stage", "Stage", STAGES),
  txt("transactionSubType", "Sub Type"),
  // Hidden — detail.
  lkc("detailType", "Detail Type", INVENTORY_TYPES),
  txt("taxableFlag", "Taxable Flag"),
  txt("arTradeDiscountFlag", "AR Trade Discount"),
  txt("arTermsDiscountFlag", "AR Terms Discount"),
  num("detailDiscount", "Detail Discount"),
  txt("unitOfMeasure", "UOM"),
  num("unitPrice", "Price"),
  txt("referenceNumber", "Reference #"),
  txt("detailComment", "Detail Comment"),
  txt("detailUserComment", "Detail User Comment"),
  num("quantityBackorder", "Backorder"),
  txt("shipMethod", "Ship Method"),
  ...udf("headerCode", "Code", "Header", 10, "txt"),
  ...udf("headerReference", "Reference", "Header", 10, "txt"),
  ...udf("headerDate", "Date", "Header", 10, "date"),
  ...udf("headerQuantity", "Quantity", "Header", 10, "num"),
  ...udf("detailCode", "Code", "Detail", 5, "txt"),
  ...udf("detailReference", "Reference", "Detail", 5, "txt"),
  ...udf("detailDate", "Date", "Detail", 5, "date"),
  ...udf("detailQuantity", "Quantity", "Detail", 5, "num"),
  ...udf("lotCode", "Code", "Lot", 10, "txt"),
  ...udf("lotReference", "Reference", "Lot", 10, "txt"),
  ...udf("lotDate", "Date", "Lot", 10, "date"),
  ...udf("lotQuantity", "Quantity", "Lot", 10, "num"),
];

// "Lot/Locations" (Delphi frmIMM0039) — location quantity summary aggregated per location.
export const LOT_LOCATION_COLUMNS = [
  txt("warehouse", "WH-Location", true),
  txt("locationCode", "Location", true),
  txt("description", "Description", true),
  num("onHand", "On Hand", true),
  num("inReceiving", "In-Receiving", true),
  num("inShipping", "In-Shipping", true),
  num("available", "Available", true),
  num("pendingRec", "Pending Rec", true),
  num("pendingShip", "Pending Ship", true),
  num("netAvailable", "Net Available", true),
  // Hidden.
  num("inBackOrder", "In-Back Order"),
  num("outBackOrder", "Out-Back Order"),
  num("inUnavailable", "In-Unavailable"),
  num("outUnavailable", "Out-Unavailable"),
  num("futureRec", "Future Rec"),
  num("futureShip", "Future Ship"),
  txt("priority", "Priority"),
  txt("classCode", "Class"),
  txt("subClassCode", "Subclass"),
  num("height", "Height"),
  num("width", "Width"),
  num("depth", "Depth"),
  txt("parentLocation", "Parent Location"),
  txt("parentLocationDescription", "Parent Location Description"),
];
